// ============================================
// Web lockdown (2026-07-15)
//
// Founder rule: there is NO way to sign into the web app. Web browsers
// (prtclapp.com desktop + mobile web) collect emails, show guides, and
// let people download guides. Every authenticated surface lives on
// native iOS exclusively.
//
// Everything here flows from the ONE switch in marketingConfig.ts:
// isProductSurfaceAllowed() (native iOS -> true, web -> WEB_AUTH_ENABLED).
// Flip WEB_AUTH_ENABLED back to true to restore web auth in one move.
//
//   - RequireNativeSurface: renders children on native; on web shows the
//     full-page App Store notice (existing-session beta users AND anon
//     visitors land here instead of the app - no way through).
//   - RedirectAuthOnWeb: renders children on native; on web redirects the
//     sign in / sign up / forgot-password routes to the landing page.
//
// The notice is a fixed full-screen overlay so it covers any app chrome
// (bottom tab bar, desktop shell) that a locked route would otherwise
// mount underneath it.
// ============================================

import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { appStoreLive, isProductSurfaceAllowed } from './marketingConfig';
import { FunnelAppStoreBadge, FunnelInlineCapture } from './MarketingCta';
import '../LandingPage.css';
import './funnel.css';

/**
 * Gentle full-page notice shown on web wherever the authenticated app
 * would have rendered. Kept kind for our earliest supporters, with a
 * clear next step (App Store when live, email capture pre-approval) and
 * no path back into the web app.
 */
export const WebAppStoreNotice: React.FC = () => {
  const live = appStoreLive();
  return (
    <div className="landing wl-lockdown" role="region" aria-label="PRTCL is on the App Store">
      <div className="wl-lockdown-inner">
        <a className="lp-wordmark" href="/">PRTCL<span className="lp-wordmark__dot">.</span></a>
        <span className="wl-eyebrow">PRTCL is an iOS app</span>
        <h1 className="wl-title">PRTCL now lives on the App Store.</h1>
        <p className="wl-lede">
          Thank you for being one of our earliest supporters. The full PRTCL
          experience now lives in the iOS app.
        </p>

        {live ? (
          <div className="wl-badge-row">
            <FunnelAppStoreBadge size="lg" page="web-lockdown" />
          </div>
        ) : (
          <div className="wl-capture">
            <p className="wl-capture-lede">
              Not on iOS yet? Leave your email and we will send you the link
              as soon as the app is available to download.
            </p>
            <FunnelInlineCapture
              page="web-lockdown"
              placement="web-lockdown"
              buttonLabel="Email me the link"
              ariaLabel="Get the App Store download note"
              successLead="Thanks. We will email you the download link as soon as the app is available."
            />
          </div>
        )}

        <nav className="wl-explore" aria-label="Free tools and guides">
          <Link to="/learn">Peptides 101</Link>
          <Link to="/tools/reconstitution-calculator">Free calculator</Link>
        </nav>
      </div>
    </div>
  );
};

/**
 * Guards the authenticated product surface. Native: renders children
 * (AuthGuard still runs inside). Web: renders the App Store notice.
 */
export const RequireNativeSurface: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isProductSurfaceAllowed()) return <WebAppStoreNotice />;
  return <>{children}</>;
};

/**
 * Wraps the sign in / sign up / forgot-password routes. Native: renders
 * children. Web: redirects to the landing page so no sign-in route works.
 */
export const RedirectAuthOnWeb: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  if (!isProductSurfaceAllowed()) return <Navigate to="/" replace />;
  return children;
};
